import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router";
import { usePageMetadata } from "@/hooks/use-page-metadata";
import { 
  Package, 
  FileText, 
  Users, 
  Settings,
  BarChart3,
  ShoppingCart,
  Pill,
  LogOut,
  Image as ImageIcon
} from "lucide-react";

export default function Admin() {
  const navigate = useNavigate();
  const { HelmetTags } = usePageMetadata("/admin");

  const handleLogout = () => {
    localStorage.removeItem("isAdminAuthenticated");
    navigate("/admin/login");
  };

  const adminSections = [
    {
      title: "Product Management",
      description: "Manage pharmaceutical products, categories, and inventory",
      icon: Package,
      path: "/admin/products",
      color: "text-blue-500",
      bgColor: "bg-blue-500/10"
    },
    {
      title: "CMS Management",
      description: "Manage page content, metadata, and SEO settings",
      icon: FileText,
      path: "/admin/cms",
      color: "text-green-500",
      bgColor: "bg-green-500/10"
    },
    {
      title: "Blog Management",
      description: "Create and manage blog posts across all categories",
      icon: FileText,
      path: "/admin/cms/blogs",
      color: "text-indigo-500",
      bgColor: "bg-indigo-500/10"
    },
    {
      title: "File Management",
      description: "Manage uploaded files and their SEO metadata",
      icon: ImageIcon,
      path: "/admin/files",
      color: "text-cyan-500",
      bgColor: "bg-cyan-500/10"
    },
    {
      title: "Analytics",
      description: "View site analytics and performance metrics",
      icon: BarChart3,
      path: "#",
      color: "text-purple-500",
      bgColor: "bg-purple-500/10"
    },
    {
      title: "Orders",
      description: "Manage customer orders and inquiries",
      icon: ShoppingCart,
      path: "#",
      color: "text-orange-500",
      bgColor: "bg-orange-500/10"
    },
    {
      title: "User Management",
      description: "Manage admin users and permissions",
      icon: Users,
      path: "#",
      color: "text-pink-500",
      bgColor: "bg-pink-500/10"
    },
    {
      title: "Settings",
      description: "Configure system settings and preferences",
      icon: Settings,
      path: "#",
      color: "text-gray-500",
      bgColor: "bg-gray-500/10"
    }
  ];

  const quickStats = [
    { label: "Total Products", value: "150+", icon: Pill, color: "text-blue-500" },
    { label: "Categories", value: "14", icon: Package, color: "text-green-500" },
    { label: "Therapeutic Areas", value: "47", icon: FileText, color: "text-purple-500" }
  ];

  return (
    <>
      <HelmetTags />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="min-h-screen"
      >
        <Navbar />
        <main className="pt-32 pb-16 min-h-screen">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8 flex justify-between items-end"
            >
              <div>
                <h1 className="text-4xl font-bold tracking-tight mb-2">Admin Dashboard</h1>
                <p className="text-muted-foreground">Manage your pharmaceutical business operations</p>
              </div>
              <Button variant="outline" onClick={handleLogout} className="gap-2">
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </motion.div>

            {/* Quick Stats */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {quickStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">{stat.label}</p>
                            <p className="text-3xl font-bold mt-1">{stat.value}</p>
                          </div>
                          <Icon className={`h-12 w-12 ${stat.color}`} />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            {/* Admin Sections */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {adminSections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <motion.div
                    key={section.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`p-3 ${section.bgColor} rounded-lg`}>
                            <Icon className={`h-6 w-6 ${section.color}`} />
                          </div>
                        </div>
                        <CardTitle className="text-xl">{section.title}</CardTitle>
                        <CardDescription>{section.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Button 
                          onClick={() => section.path !== "#" && navigate(section.path)}
                          className="w-full"
                          disabled={section.path === "#"}
                        >
                          {section.path === "#" ? "Coming Soon" : "Manage"}
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </main>
        <Footer />
      </motion.div>
    </>
  );
}